/**
 * TtsPlayer — HTML5 Audio 로 TTS 오디오 재생 (Plan 05-05 Task 1 / WIDGET-03 소비자).
 *
 * 동작:
 *   - play(url): 현재 재생 중이면 stop() → new Audio(url) → audio.play() → 'ended' 대기
 *   - stop(): 현재 재생 중이면 pause + currentTime=0 + src reset
 *   - 'error' 이벤트 → reject (caller 는 idle 로 복귀)
 *
 * 중복 재생 방지 (DESIGN §3.6 엣지 "overlap 금지"):
 *   이전 재생이 완료되기 전에 새 play() 가 호출되면 이전 audio 는 즉시 pause · currentTime=0
 *   으로 중단 — 새 audio 가 바로 재생 시작. (큐잉은 events/handlers 쪽 state orchestrator 책임.)
 *
 * 접근성 (WIDGET-08 / DESIGN §10.4):
 *   TTS 는 prefers-reduced-motion 과 독립 — 시각적 모션 강등과 별개로 오디오/자막 병행은 유지.
 */
export class TtsPlayer {
  private current: HTMLAudioElement | null = null;
  private currentReject: ((err: Error) => void) | null = null;

  play(audioUrl: string): Promise<void> {
    // 1) 이전 재생 중단 (stop 은 null-safe)
    this.stop();

    // 2) 새 Audio 요소 생성
    const audio = new Audio(audioUrl);
    this.current = audio;

    return new Promise<void>((resolve, reject) => {
      this.currentReject = reject;
      const onEnded = (): void => {
        cleanup();
        // current 가 아직 이 audio 인 경우에만 초기화
        if (this.current === audio) {
          this.current = null;
          this.currentReject = null;
        }
        resolve();
      };
      const onError = (): void => {
        cleanup();
        if (this.current === audio) {
          this.current = null;
          this.currentReject = null;
        }
        reject(new Error('audio_error'));
      };
      const cleanup = (): void => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
      };
      audio.addEventListener('ended', onEnded, { once: true });
      audio.addEventListener('error', onError, { once: true });
      try {
        // HTMLAudioElement.play() 는 Promise<void> 반환. 일부 구형 브라우저는
        // undefined 을 반환할 수 있지만 OBS 크롬 105+ 는 항상 Promise.
        const p: unknown = audio.play();
        if (p !== null && typeof p === 'object' && 'then' in p) {
          void (p as Promise<void>).catch((err: unknown) => {
            cleanup();
            if (this.current === audio) {
              this.current = null;
              this.currentReject = null;
            }
            reject(err instanceof Error ? err : new Error('audio_play_failed'));
          });
        }
      } catch (err) {
        cleanup();
        if (this.current === audio) {
          this.current = null;
          this.currentReject = null;
        }
        reject(err instanceof Error ? err : new Error('audio_play_threw'));
      }
    });
  }

  stop(): void {
    if (!this.current) return;
    const a = this.current;
    try {
      a.pause();
    } catch {
      // ignore
    }
    try {
      a.currentTime = 0;
    } catch {
      // ignore
    }
    // play() 가 아직 대기 중이면 즉시 reject 로 해제하여 caller hang 방지
    const rej = this.currentReject;
    this.current = null;
    this.currentReject = null;
    if (rej) rej(new Error('interrupted'));
  }
}

/** 일회성 재생 헬퍼 — throw-away TtsPlayer 를 만들어 play. */
export function playTts(audioUrl: string): Promise<void> {
  return new TtsPlayer().play(audioUrl);
}
